import React from 'react';
import {
  FormInstance,
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  TimePicker,
} from 'antd';
import moment, { Moment } from 'moment';

import { TimekeepingHolidayFragment } from '@common/graphql/schema';
import { BaseButton } from '@common/components';
import { useMutation } from '@common/hooks';
import { UpsertHoliday } from '@timekeeping/graphql';
import { createMessage } from 'works/legacy/lib/message-helper';

type FormValues = {
  startDay: moment.Moment;
  endDay: moment.Moment;
  startTime: moment.Moment;
  endTime: moment.Moment;
};

export const HolidayForm = ({
  form,
  editedHoliday,
  onClose,
}: {
  form: FormInstance;
  editedHoliday: TimekeepingHolidayFragment | null;
  onClose: () => void;
}) => {
  const [submitEdits] = useMutation(UpsertHoliday);

  const onSubmit = async (values: FormValues) => {
    try {
      await submitEdits({
        variables: {
          id: form.getFieldValue('id'),
          input: {
            startDay: values.startDay.toISOString(),
            startTime: values.startTime.format('HH:mm'),
            endDay: values.endDay.toISOString(),
            endTime: values.endTime.format('HH:mm'),
          },
        },
      }).then((res) => {
        return res.data?.update && !res.data.update.errors?.length
          ? Promise.resolve(res)
          : Promise.reject(res.errors || res.data?.update?.errors);
      });

      createMessage('success', 'Holiday successfully updated.');
      onClose();
    } catch (e) {
      createMessage('error', 'Failed to update holiday. Please try again.');
    }
  };

  const source = editedHoliday?.originEvent || editedHoliday;
  const minStartDay = moment(source?.startDay).subtract(1, 'day');
  const maxEndDay = moment(source?.endDay).add(1, 'day');

  const disabledDate = (date: Moment) =>
    date.isBefore(minStartDay, 'day') || date.isAfter(maxEndDay, 'day');

  return (
    <>
      <Form
        className="timesheet-holidays-form"
        onFinish={onSubmit}
        layout="vertical"
        form={form}
      >
        <Row gutter={[16, 0]}>
          <Col span={8}>
            <Form.Item name="holidayName" label="Holiday Name" required>
              <Input
                size="large"
                placeholder="Type the holiday name"
                disabled
              />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item name="startDay" label="Start Day" required>
              <DatePicker
                size="large"
                className="select-width"
                name="picker-start-day"
                placeholder="Select start day"
                format="MM/DD"
                disabledDate={disabledDate}
              />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item name="startTime" label="Start Time" required>
              <TimePicker
                size="large"
                className="select-width"
                name="picker-start-time"
                placeholder="Select start time"
                format="HH:mm"
              />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item name="endDay" label="End Day" required>
              <DatePicker
                size="large"
                className="select-width"
                name="picker-end-day"
                placeholder="Select end day"
                format="MM/DD"
                disabledDate={disabledDate}
              />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item name="endTime" label="End Time" required>
              <TimePicker
                size="large"
                className="select-width"
                name="picker-end-time"
                placeholder="Select end time"
                format="HH:mm"
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[12, 0]}>
          <Col>
            <BaseButton
              type="default"
              size="large"
              onClick={onClose}
              disabled={false}
              loading={false}
            >
              Cancel
            </BaseButton>
          </Col>
          <Col>
            <BaseButton
              type="primary"
              size="large"
              onClick={form.submit}
              disabled={false}
              loading={false}
            >
              Save
            </BaseButton>
          </Col>
        </Row>
      </Form>
      <style jsx>{`
        :global(.timesheet-holidays-form) {
          padding-top: 16px;
          padding-bottom: 16px;
        }
        :global(.timesheet-holidays-form .select-width) {
          width: 100%;
        }
      `}</style>
    </>
  );
};
